import React, { useCallback, useEffect, useRef } from "react";

import { IgBasePrimitives } from "@intelligems/ig-design-tokens/constants";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { useTheme } from "@mui/material";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { useImmer } from "use-immer";

import type { WorkspaceTabSpec } from "../../domainConfig";

const BOTTOM_TABS_DEFAULT_HEIGHT = 280;
const BOTTOM_TABS_MIN_HEIGHT = 160;
const BOTTOM_TABS_MAX_HEIGHT = 800;
const TAB_ACCENT_COLOR = IgBasePrimitives["A200"];
const BOTTOM_TABS_KEYBOARD_RESIZE_STEP = 16;
const BOTTOM_TABS_KEYBOARD_RESIZE_PAGE_STEP = 64;
const BOTTOM_SECTION_TAB_FONT_SIZE = 12;

interface BottomSectionProps<TabKey extends string> {
  selectedTab: TabKey;
  onSelectedTabChange: (tab: TabKey) => void;
  /**
   * The domain's tabs. An `unavailable` tab still renders and is still
   * clickable — a truly `disabled` MUI `Tab` swallows pointer events, leaving
   * nowhere to explain itself, and a tooltip would be invisible to keyboard and
   * touch users. The reason goes in the panel instead.
   */
  tabs: readonly WorkspaceTabSpec<TabKey>[];
  /**
   * The space the panel is actually allowed to occupy, measured from the split
   * container by the workspace. Resizing clamps to it, so the panel can never
   * push the code column past its container — the cause of the workspace
   * growing a second scrollbar when the panel was dragged tall. `undefined`
   * (tests, embedders that do not measure) falls back to the static ceiling.
   */
  maxHeight?: number | undefined;
}

const clampBottomTabsHeight = (height: number, maxHeight?: number) => {
  const ceiling = Math.max(
    BOTTOM_TABS_MIN_HEIGHT,
    Math.min(BOTTOM_TABS_MAX_HEIGHT, maxHeight ?? BOTTOM_TABS_MAX_HEIGHT)
  );

  return Math.min(
    ceiling,
    Math.max(BOTTOM_TABS_MIN_HEIGHT, Math.round(height))
  );
};

const BottomTabLabel = <TabKey extends string>({
  tab,
}: {
  tab: WorkspaceTabSpec<TabKey>;
}) => {
  const isUnavailable = tab.availability.kind === "unavailable";
  const badge =
    tab.availability.kind === "enabled" ? tab.availability.badge : undefined;

  return (
    <Stack component="span" direction="row" alignItems="center">
      {tab.label}
      {typeof badge === "number" && (
        <Stack
          component="span"
          alignItems="center"
          justifyContent="center"
          sx={{
            ml: 0.75,
            px: 0.875,
            minWidth: 26,
            height: 20,
            borderRadius: 999,
            backgroundColor: "action.selected",
          }}
        >
          {badge}
        </Stack>
      )}
      {isUnavailable && (
        <Stack
          component="span"
          alignItems="center"
          justifyContent="center"
          sx={{
            ml: 0.75,
            px: 0.875,
            minWidth: 26,
            height: 20,
            borderRadius: 999,
            backgroundColor: "action.hover",
          }}
        >
          Soon
        </Stack>
      )}
    </Stack>
  );
};

/**
 * Zone 4 — the resizable bottom tab strip and its panel. Renders the selected
 * tab's content directly from the domain's tab specs: an `enabled` tab supplies
 * a renderer, an `unavailable` tab supplies a reason. Because those are the only
 * two variants, a tab can never present itself as present and then render an
 * empty pane.
 */
export const BottomSection = <TabKey extends string>({
  selectedTab,
  onSelectedTabChange,
  tabs,
  maxHeight,
}: BottomSectionProps<TabKey>) => {
  const theme = useTheme();
  const [bottomTabsHeight, setBottomTabsHeight] = useImmer(
    BOTTOM_TABS_DEFAULT_HEIGHT
  );
  const bottomTabsHeightRef = useRef(BOTTOM_TABS_DEFAULT_HEIGHT);
  const bottomTabsResizeCleanupRef = useRef<(() => void) | null>(null);

  // The live ceiling is read through a ref so an in-flight pointer drag clamps
  // against the container's *current* size rather than the size it had when the
  // drag started.
  const maxHeightRef = useRef(maxHeight);
  maxHeightRef.current = maxHeight;

  const setClampedBottomTabsHeight = useCallback((height: number) => {
    const nextHeight = clampBottomTabsHeight(height, maxHeightRef.current);
    bottomTabsHeightRef.current = nextHeight;
    setBottomTabsHeight(nextHeight);
  }, []);

  const resizeBottomTabsBy = useCallback(
    (delta: number) => {
      setClampedBottomTabsHeight(bottomTabsHeightRef.current + delta);
    },
    [setClampedBottomTabsHeight]
  );

  const handleBottomTabsResizePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault();

      bottomTabsResizeCleanupRef.current?.();

      const startY = event.clientY;
      const startHeight = bottomTabsHeightRef.current;

      const handlePointerMove = (moveEvent: PointerEvent) => {
        setClampedBottomTabsHeight(startHeight + startY - moveEvent.clientY);
      };

      const handlePointerUp = () => {
        bottomTabsResizeCleanupRef.current?.();
      };

      document.addEventListener("pointermove", handlePointerMove);
      document.addEventListener("pointerup", handlePointerUp);

      bottomTabsResizeCleanupRef.current = () => {
        document.removeEventListener("pointermove", handlePointerMove);
        document.removeEventListener("pointerup", handlePointerUp);
        bottomTabsResizeCleanupRef.current = null;
      };
    },
    [setClampedBottomTabsHeight]
  );

  const handleBottomTabsResizeKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      switch (event.key) {
        case "ArrowUp":
          event.preventDefault();
          resizeBottomTabsBy(BOTTOM_TABS_KEYBOARD_RESIZE_STEP);
          break;
        case "ArrowDown":
          event.preventDefault();
          resizeBottomTabsBy(-BOTTOM_TABS_KEYBOARD_RESIZE_STEP);
          break;
        case "PageUp":
          event.preventDefault();
          resizeBottomTabsBy(BOTTOM_TABS_KEYBOARD_RESIZE_PAGE_STEP);
          break;
        case "PageDown":
          event.preventDefault();
          resizeBottomTabsBy(-BOTTOM_TABS_KEYBOARD_RESIZE_PAGE_STEP);
          break;
        case "Home":
          event.preventDefault();
          setClampedBottomTabsHeight(BOTTOM_TABS_MIN_HEIGHT);
          break;
        case "End":
          event.preventDefault();
          setClampedBottomTabsHeight(BOTTOM_TABS_MAX_HEIGHT);
          break;
      }
    },
    [resizeBottomTabsBy, setClampedBottomTabsHeight]
  );

  // A shrinking container (window resize, a taller gate strip) re-clamps the
  // panel instead of letting it overflow the column.
  useEffect(() => {
    setClampedBottomTabsHeight(bottomTabsHeightRef.current);
  }, [maxHeight, setClampedBottomTabsHeight]);

  useEffect(
    () => () => {
      bottomTabsResizeCleanupRef.current?.();
    },
    []
  );

  const active = tabs.find((tab) => tab.key === selectedTab);

  return (
    <Stack
      data-testid="bottom-tabs-panel"
      sx={{
        height: bottomTabsHeight,
        minHeight: BOTTOM_TABS_MIN_HEIGHT,
        maxHeight: BOTTOM_TABS_MAX_HEIGHT,
        // Owns its own overflow: the panel is a fixed-height region, never a
        // reason for an ancestor to scroll.
        overflow: "hidden",
        borderTop: 1,
        borderColor: "divider",
        backgroundColor: "background.paper",
        flexShrink: 0,
      }}
    >
      <Stack
        role="separator"
        aria-label="Resize bottom tabs"
        aria-orientation="horizontal"
        aria-valuemin={BOTTOM_TABS_MIN_HEIGHT}
        aria-valuemax={clampBottomTabsHeight(BOTTOM_TABS_MAX_HEIGHT, maxHeight)}
        aria-valuenow={bottomTabsHeight}
        tabIndex={0}
        onPointerDown={handleBottomTabsResizePointerDown}
        onKeyDown={handleBottomTabsResizeKeyDown}
        sx={{
          height: 6,
          flexShrink: 0,
          cursor: "row-resize",
          touchAction: "none",
          alignItems: "center",
          justifyContent: "center",
          "&::before": {
            content: '""',
            width: 44,
            height: 2,
            borderRadius: 1,
            backgroundColor: theme.igPrimitives.gray[900],
          },
          "&:hover::before, &:focus-visible::before": {
            backgroundColor: TAB_ACCENT_COLOR,
          },
          "&:focus-visible": {
            outline: `2px solid ${TAB_ACCENT_COLOR}`,
            outlineOffset: -2,
          },
        }}
      />
      <Tabs
        value={selectedTab}
        onChange={(_, value: TabKey) => onSelectedTabChange(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          minHeight: 36,
          flexShrink: 0,
          px: 1.5,
          borderBottom: 0,
          "& .MuiTab-root": {
            minHeight: 36,
            minWidth: "auto",
            px: 2,
            py: 0,
            fontSize: BOTTOM_SECTION_TAB_FONT_SIZE,
          },
        }}
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.key}
            value={tab.key}
            aria-disabled={
              tab.availability.kind === "unavailable" ? true : undefined
            }
            label={<BottomTabLabel tab={tab} />}
            sx={
              tab.availability.kind === "unavailable"
                ? { color: "text.disabled" }
                : undefined
            }
          />
        ))}
      </Tabs>
      <Stack sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        {active?.availability.kind === "enabled" ? (
          active.availability.render()
        ) : active ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ p: 3, height: "100%", textAlign: "center" }}
          >
            <IgTypography color="text.secondary">
              {active.availability.reason}
            </IgTypography>
          </Stack>
        ) : null}
      </Stack>
    </Stack>
  );
};
